import type { CSSProperties, ReactNode } from "react";
import { BuyerFields } from "@libs/identity-ui";
import type { BuyerFieldDefinition } from "@libs/identity-ui";
import type { Buyer, BuyerField } from "@libs/buyer-identity-client";
import { buyerDetailSummary, buyerDetailsShown } from "@libs/buyer-identity-client/requirements";
import { button, input, size, text } from "./ui";
import { UNDELIVERABLE_RED } from "./undeliverable";

/** What is wrong with a field the buyer filled in (a postcode that is not
 *  one of their country's), said under it in red once they have left it. */
export function FieldProblem({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      style={{ color: UNDELIVERABLE_RED, fontSize: 13, lineHeight: 1.35, margin: 0 }}
    >
      {children}
    </p>
  );
}

/** Shared checkout profile section. The provider declares the fields;
 * identity owns their values. The checkout controls editing and saving.
 * What the profile already gives is one summary with one Edit; only the
 * fields it lacks are asked for, under it. */
export function BuyerDetails({
  fields,
  value,
  saved = value,
  missing,
  editing,
  checkNames = false,
  disabled,
  onEdit,
  onChange,
  onBlurField,
  notes,
}: {
  fields: readonly BuyerFieldDefinition[];
  value: Partial<Buyer>;
  /** Saved values decide which optional fields stay open. Draft edits must
   *  not turn a field into a summary while the buyer is typing in it. */
  saved?: Partial<Buyer>;
  /** The fields the saved profile lacks (`missingBuyerFields`): the ones
   *  asked for. Every other field is summarised until it is edited. */
  missing: readonly BuyerField[];
  editing: boolean;
  /** The first and last name on the form are split from the whole name the
   *  profile saved (`suggestsBuyerNames`): the buyer is asked to check them. */
  checkNames?: boolean;
  disabled: boolean;
  onEdit: (editing: boolean) => void;
  onChange: (value: Partial<Buyer>) => void;
  /** A typed field was left (its input lost focus). */
  onBlurField?: (field: BuyerFieldDefinition["id"]) => void;
  /** Said under a field (a country the checkout cannot deliver to); under
   *  the summary while the field is in it. */
  notes?: Partial<Record<BuyerField, ReactNode>>;
}) {
  if (!fields.length) return null;
  const { known, summarised, asked } = buyerDetailsShown({ fields, missing, value, saved, editing });
  return (
    <section
      aria-label="Your details"
      style={{ display: "grid", gap: "var(--lc-space)" }}
    >
      <div
        style={{
          alignItems: "baseline",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span style={{ ...text.strong, fontSize: size.small }}>
          Your details
        </span>
        {known.length > 0 && editing ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onEdit(false)}
            style={{ ...button.link, textDecoration: "none" }}
          >
            Done
          </button>
        ) : null}
      </div>
      {checkNames ? (
        <p style={text.hint}>Please check your first and last name.</p>
      ) : null}
      {summarised ? (
        <div style={summary}>
          <div style={{ display: "grid", gap: 2, minWidth: 0 }}>
            {buyerDetailSummary({ fields: known }, value).map((line) => (
              <span
                key={line.key}
                style={line.strong ? text.strong : text.hint}
              >
                {line.text}
              </span>
            ))}
          </div>
          <button
            type="button"
            aria-label="Edit"
            disabled={disabled}
            onClick={() => onEdit(true)}
            style={edit}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M10.6 2.9 13.1 5.4M2.5 13.5l.6-3.1 7.9-7.9a1.2 1.2 0 0 1 1.7 0l.8.8a1.2 1.2 0 0 1 0 1.7l-7.9 7.9-3.1.6Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      ) : null}
      {summarised
        ? known
            .filter((field) => !asked.includes(field) && notes?.[field.id])
            .map((field) => <div key={field.id}>{notes?.[field.id]}</div>)
        : null}
      {asked.length ? (
        <BuyerFields
          notes={notes}
          fields={asked}
          value={value}
          disabled={disabled}
          onChange={onChange}
          onBlur={onBlurField}
          gap="calc(var(--lc-space) * 1.25)"
          labelStyle={{ ...text.label, gap: "calc(var(--lc-space) * 0.5)" }}
          inputStyle={field}
        />
      ) : null}
    </section>
  );
}

const field: CSSProperties = {
  ...input,
  fontSize: "var(--lc-input-font-size)",
  outline: "none",
};

const summary: CSSProperties = {
  ...input,
  alignItems: "center",
  display: "grid",
  gap: "var(--lc-space)",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  padding: "calc(var(--lc-space) * 1.25) calc(var(--lc-space) * 1.5)",
};

/** The summary's one Edit: a pencil, with room around it for a thumb. */
const edit: CSSProperties = {
  ...button.link,
  alignItems: "center",
  display: "inline-flex",
  height: 32,
  justifyContent: "center",
  margin: "-4px calc(var(--lc-space) * -0.75) -4px 0",
  width: 32,
};
